import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, Users, Clock, Target, CheckCircle, AlertTriangle, Calendar, DollarSign } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';

const MetricsOverview = () => {
  const { dashboardData, loading, dateFilter, setDateFilter } = useDashboard();

  // Memoize metrics data calculation to prevent unnecessary re-computations
  const metricsData = useMemo(() => {
    if (!dashboardData?.metrics) return [];

    const { metrics } = dashboardData;

    return [
      // Top row - 3 metrics
      {
        id: 1,
        label: 'Total Projects',
        value: metrics.totalProjects?.toString() || '0',
        change: `${metrics.completedProjects || 0} completed`,
        changeType: 'positive',
        icon: Target,
        description: 'total projects in system'
      },
      {
        id: 2,
        label: 'Project Success Rate',
        value: `${metrics.successRate || 0}%`,
        change: (metrics.successRate || 0) >= 80 ? 'Excellent' : 'Good',
        changeType: (metrics.successRate || 0) >= 80 ? 'positive' : 'neutral',
        icon: CheckCircle,
        description: 'projects completed successfully'
      },
      {
        id: 3,
        label: 'Time to Value',
        value: `${metrics.timeToValue || 0} days`,
        change: (metrics.timeToValue || 0) <= 30 ? 'On track' : 'Needs attention',
        changeType: (metrics.timeToValue || 0) <= 30 ? 'positive' : 'negative',
        icon: Clock,
        description: 'average milestone completion'
      },
      // Bottom row - 3 metrics
      {
        id: 4,
        label: 'Team Size',
        value: metrics.teamSize?.toString() || '0',
        change: 'Active members',
        changeType: 'neutral',
        icon: Users,
        description: 'team members'
      },
      {
        id: 5,
        label: 'Team Utilization',
        value: `${metrics.utilization || 0}%`,
        change: (metrics.utilization || 0) >= 80 ? 'Optimal' : 'Below target',
        changeType: (metrics.utilization || 0) >= 80 ? 'positive' : 'negative',
        icon: Users,
        description: 'current average'
      },
      {
        id: 6,
        label: 'Total Hours',
        value: `${dashboardData?.details?.utilization?.totalBillableHours?.toFixed(1) || '0'}h`,
        change: `${dashboardData?.details?.utilization?.totalBillableHours?.toFixed(1) || '0'}h billable`,
        changeType: 'positive',
        icon: DollarSign,
        description: 'billable hours tracked'
      }
    ];
  }, [dashboardData?.metrics, dashboardData?.details?.utilization]);

  // Memoize change icon component
  const getChangeIcon = useMemo(() => (changeType) => {
    switch (changeType) {
      case 'positive':
        return <TrendingUp size={16} />;
      case 'negative':
        return <TrendingDown size={16} />;
      default:
        return <Minus size={16} />;
    }
  }, []);

  if (loading) {
    return <div className="loading">Loading metrics...</div>;
  }

  if (!dashboardData) {
    return <div className="loading">No data available</div>;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Key Metrics Overview</h2>
        <div className="card-actions">
          <select 
            className="time-filter"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="last7days">Last 7 days</option>
            <option value="last30days">Last 30 days</option>
            <option value="last90days">Last 90 days</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
          </select>
        </div>
      </div>
      
      <div className="metric-grid-3x2">
        {metricsData.map((metric) => {
          const IconComponent = metric.icon;
          return (
            <div key={metric.id} className="metric-card">
              <div className="metric-header">
                <IconComponent size={20} color="#64748b" />
                <span className="metric-label">{metric.label}</span>
              </div>
              <div className="metric-value">{metric.value}</div>
              <div className={`metric-change ${metric.changeType}`}>
                {getChangeIcon(metric.changeType)}
                <span>{metric.change}</span>
                <span className="metric-description">{metric.description}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MetricsOverview;
